import avenPlan from 'assets/aven/aven-plan.jpg';
import avenPlanPlaceholder from 'assets/aven/aven-plan-placeholder.jpg';
import avenToday from 'assets/aven/aven-today.jpg';
import avenTodayPlaceholder from 'assets/aven/aven-today-placeholder.jpg';
import labyrinthHero from 'assets/labyrinth/labyrinth-hero.png';
import labyrinthHeroLarge from 'assets/labyrinth/labyrinth-hero-large.png';
import labyrinthHeroPlaceholder from 'assets/labyrinth/labyrinth-hero-placeholder.png';
import spaceBoard from 'assets/space/space-board.png';
import spaceBoardLarge from 'assets/space/space-board-large.png';
import spaceBoardPlaceholder from 'assets/space/space-board-placeholder.png';
import {Footer} from 'components/Footer';
import {Meta} from 'components/Meta';
import {Intro} from 'layouts/Home/Intro';
import {Profile} from 'layouts/Home/Profile';
import {ProjectSummary} from 'layouts/Home/ProjectSummary';
import {ToolFamily} from 'layouts/Tools/Tools';
import {useEffect, useRef, useState} from 'react';
import styles from './Home.module.css';

const disciplines = ['Machine Learning', 'Artificial Intelligence', 'Architecture', 'Cloud', 'Hardware','System Design','Developer'];

export const Home = () => {
    const [visibleSections, setVisibleSections] = useState([]);
    const [scrollIndicatorHidden, setScrollIndicatorHidden] = useState(false);
    const intro = useRef();
    const projectOne = useRef();
    const projectTwo = useRef();
    const projectThree = useRef();
    const toolFamily = useRef();
    const details = useRef();

    useEffect(() => {
        const sections = [intro, projectOne, projectTwo, projectThree, toolFamily, details];

        const sectionObserver = new IntersectionObserver(
            (entries, observer) => {
                entries.forEach(entry => {
                    if (entry.isIntersecting) {
                        const section = entry.target;
                        observer.unobserve(section);
                        // Track ids: section refs can't be read during render
                        setVisibleSections(prevSections =>
                            prevSections.includes(section.id) ? prevSections : [...prevSections, section.id]
                        );
                    }
                });
            },
            {rootMargin: '0px 0px -10% 0px', threshold: 0.1}
        );

        const indicatorObserver = new IntersectionObserver(
            ([entry]) => {
                setScrollIndicatorHidden(!entry.isIntersecting);
            },
            {rootMargin: '-100% 0px 0px 0px'}
        );

        sections.forEach(section => {
            sectionObserver.observe(section.current);
        });

        indicatorObserver.observe(intro.current);

        return () => {
            sectionObserver.disconnect();
            indicatorObserver.disconnect();
        };
    }, []);

    return (
        <div className={styles.home}>
            <Meta
                title="Engineer"
                description="Portfolio of Tim McDermott, a senior software engineer in Wellington building developer tools, infrastructure and machine learning systems."
            />
            <Intro
                id="intro"
                sectionRef={intro}
                disciplines={disciplines}
                scrollIndicatorHidden={scrollIndicatorHidden}
            />
            <ProjectSummary
                id="project-1"
                sectionRef={projectOne}
                visible={visibleSections.includes('project-1')}
                index={1}
                title="space"
                description="space puts worktrees from several repos in one disposable workspace, branches them together, and tears it down without touching the source repos"
                buttonText="View project"
                buttonLink="/projects/space/"
                model={{
                    type: 'laptop',
                    alt: 'The space dashboard board: two areas of workspaces, each card showing its set, member repos, branches and whether an agent is running',
                    textures: [
                        {
                            srcSet: [spaceBoard, spaceBoardLarge],
                            placeholder: spaceBoardPlaceholder,
                        },
                    ],
                }}
            />
            <ProjectSummary
                id="project-2"
                alternate
                sectionRef={projectTwo}
                visible={visibleSections.includes('project-2')}
                index={2}
                title="Aven"
                description="A mobile app that plans your week around the routines you keep"
                buttonText="Under development"
                model={{
                    type: 'phone',
                    alt: 'Two phones showing Aven: a day as a timeline under a week strip, and the week planned as a grid',
                    textures: [
                        {
                            srcSet: [avenToday, avenToday],
                            placeholder: avenTodayPlaceholder,
                        },
                        {
                            srcSet: [avenPlan, avenPlan],
                            placeholder: avenPlanPlaceholder,
                        },
                    ],
                }}
            />
            <ProjectSummary
                id="project-3"
                sectionRef={projectThree}
                visible={visibleSections.includes('project-3')}
                index={3}
                title="Labyrinth"
                description="Labyrinth is a declarative homelab: lab.yml describes a two-node Proxmox cluster, and one command builds it"
                buttonText="View project"
                buttonLink="/projects/labyrinth/"
                model={{
                    type: 'laptop',
                    alt: 'The Labyrinth site: a declarative homelab, with a terminal showing one command converging the whole cluster',
                    textures: [
                        {
                            srcSet: [labyrinthHero, labyrinthHeroLarge],
                            placeholder: labyrinthHeroPlaceholder,
                        },
                    ],
                }}
            />
            <ToolFamily
                id="tools"
                sectionRef={toolFamily}
                visible={visibleSections.includes('tools')}
            />
            <Profile
                sectionRef={details}
                visible={visibleSections.includes('details')}
                id="details"
            />
            <Footer/>
        </div>
    );
};
